import { Modal } from '../../../../../ui/components/pure/modal/modal.component';
import { TwoPaneModalContent } from '../../../../../ui/components/pure/modal-content/two-pane-modal-content.component';
import { DialogActions, FormHelperText, FormLabel, Grid, RadioGroup, useMediaQuery, useTheme } from '@mui/material';
import { FormControl } from '../../../../../ui/components/pure/form-control/form-control.component';
import { TextField } from '../../../../../ui/components/pure/text-field/text-field.component';
import { InviteTeamMemberReactFormState, useInviteTeamMemberAction } from './invite-team-member-action.hook';
import { Controller, useForm } from 'react-hook-form';
import { mobilePhoneValidator } from '../../../../../ui/util/mobile-phone.validator';
import { useMobilePhoneFormatter } from '../../../../../ui/formatters/mobile-phone.formatter';
import { TextButton } from '../../../../../ui/components/pure/text-button/text-button.component';
import { useInviteMemberModalStyles } from './invite-team-member-modal.styles';
import { Alert, AlertTitle } from '@mui/material';
import { parseErrorMessage } from '../../../../../core/api/api.context';
import { useQueryGetProject } from '../../../../../core/queries/projects.queries';
import { AccessOption } from '../../../components/access-option.component';
import { useAccessLevelDescriptions } from '../../../hooks/use-access-level-descriptions.hook';
import { accessLevelsForProjectType } from '../../../details/logic/team-member-details.constants';
import validateEmail from 'validator/lib/isEmail';

export interface InviteTeamMemberModalProps {
  projectID: string;
  onAbort(): void;
  onSuccess(): void;
}

export interface InviteTeamMemberModalComponentProps {
  projectID: string;
  state: ReturnType<typeof useInviteTeamMemberAction>[0];
  handlers: ReturnType<typeof useInviteTeamMemberAction>[1];
}

export function InviteTeamMemberModal(props: InviteTeamMemberModalProps) {
  const [state, handlers] = useInviteTeamMemberAction(props);

  return <InviteTeamMemberModalComponent projectID={props.projectID} state={state} handlers={handlers} />;
}

export function InviteTeamMemberModalComponent(props: InviteTeamMemberModalComponentProps) {
  const classes = useInviteMemberModalStyles();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { control, handleSubmit, formState, watch } = useForm<InviteTeamMemberReactFormState>({
    defaultValues: { accessLevel: null }
  });
  const mobile = watch('mobile');
  const email = watch('email');

  const mobilePhoneFormatter = useMobilePhoneFormatter(props.projectID);
  // Which roles this project can hold — a carrier or region project offers its own set.
  const project = useQueryGetProject(props.projectID);
  const accessLevels = accessLevelsForProjectType(project.data?.projectType);
  const descriptions = useAccessLevelDescriptions(props.projectID);

  return (
    <Modal
      title={'Invite Team Member'}
      open={true}
      loading={props.state.saving}
      maxWidth="md"
      onClose={props.handlers.onCancel}
    >
      <form noValidate autoComplete="off" onSubmit={handleSubmit(props.handlers.onSubmit)}>
        <TwoPaneModalContent
          leftDesktopWidth={6}
          rigthDesktopWidth={6}
          flexibleWidthPane={'right'}
          leftContent={() => (
            <Grid container sx={{ flexDirection: 'column' }} style={{ padding: '0px 16px', width: '100%', margin: 0 }}>
              <Grid container spacing={isXs ? 0 : 6}>
                <Grid
                  style={{ marginBottom: '10px' }}
                  size={{
                    xs: 12,
                    sm: 6
                  }}
                >
                  <FormControl
                    control={control}
                    name="firstName"
                    required
                    ComponentProps={{
                      textFieldVariant: 'floating' as const,
                      placeholder: 'First name',
                      style: { width: '100%' }
                    }}
                    Component={TextField}
                  />
                </Grid>
                <Grid
                  style={{ marginBottom: '10px' }}
                  size={{
                    xs: 12,
                    sm: 6
                  }}
                >
                  <FormControl
                    control={control}
                    name="lastName"
                    Component={TextField}
                    ComponentProps={{
                      textFieldVariant: 'floating' as const,
                      placeholder: 'Last name',
                      style: { width: '100%' }
                    }}
                    required
                  />
                </Grid>
              </Grid>
              <Grid style={{ marginBottom: '10px' }} size={12}>
                <FormControl
                  control={control}
                  name="role"
                  required
                  Component={TextField}
                  ComponentProps={{
                    textFieldVariant: 'floating' as const,
                    placeholder: 'Role',
                    style: { width: '100%' }
                  }}
                />
              </Grid>
              <Grid style={{ marginBottom: '10px' }} size={12}>
                <FormControl
                  control={control}
                  name="email"
                  rules={{
                    validate: (email) =>
                      !!email && !validateEmail(email) ? 'Email must be a valid email address' : true
                  }}
                  required={!mobile}
                  Component={TextField}
                  ComponentProps={{
                    type: 'email',
                    textFieldVariant: 'floating' as const,
                    placeholder: 'Email address',
                    style: { width: '100%' }
                  }}
                />
              </Grid>
              <Grid style={{ marginBottom: '10px' }} size={12}>
                <FormControl
                  control={control}
                  name="mobile"
                  required={!email}
                  rules={{
                    validate: (mobile: string | undefined) => !mobile || mobilePhoneValidator(mobile)
                  }}
                  setValueAs={(mobile) => {
                    return mobilePhoneFormatter(mobile);
                  }}
                  Component={TextField}
                  ComponentProps={{
                    type: 'tel',
                    textFieldVariant: 'floating' as const,
                    placeholder: 'Mobile number (required for Kiosk deliveries)',
                    style: { width: '100%' }
                  }}
                />
              </Grid>
              {/* External ID is typed as string intentionally — kiosks currently accept numbers only but the field is designed to support arbitrary string identifiers */}
              <Grid style={{ marginBottom: '10px' }} size={12}>
                <FormControl
                  control={control}
                  name="externalID"
                  Component={TextField}
                  ComponentProps={{
                    textFieldVariant: 'floating' as const,
                    placeholder: 'External ID',
                    style: { width: '100%' }
                  }}
                />
              </Grid>
              <Grid style={{ marginBottom: '10px' }} size={12}>
                <FormControl
                  control={control}
                  name="note"
                  Component={TextField}
                  ComponentProps={{
                    textFieldVariant: 'floating' as const,
                    placeholder: 'Note',
                    style: { width: '100%' }
                  }}
                />
              </Grid>
            </Grid>
          )}
          rightContent={() => (
            <Grid container sx={{ flexDirection: 'column' }} style={{ padding: '16px' }}>
              <FormLabel
                component="legend"
                required
                error={!!formState.errors.accessLevel}
                style={{ marginBottom: '10px' }}
              >
                Access Level
              </FormLabel>
              <Controller
                control={control}
                name="accessLevel"
                rules={{ required: true }}
                render={({ field }) => (
                  <RadioGroup {...field} className={classes.AccessLevelRadioGroup}>
                    {accessLevels.map((level) => (
                      <AccessOption key={level} level={level} description={descriptions[level]} classes={classes} />
                    ))}
                  </RadioGroup>
                )}
              />
              {formState.errors.accessLevel ? (
                <FormHelperText style={{ color: 'red' }}>Please select an option</FormHelperText>
              ) : null}
              {props.state.error && (
                <Grid>
                  <Alert variant="outlined" severity="error">
                    <AlertTitle>Something went wrong</AlertTitle>
                    {parseErrorMessage(props.state.error.message)}
                  </Alert>
                </Grid>
              )}
            </Grid>
          )}
        />
        <DialogActions style={{ borderTop: '1px solid #EBEDF2', padding: '16px 24px' }}>
          <TextButton color="secondary" variant="outlined" onClick={props.handlers.onCancel}>
            Cancel
          </TextButton>
          <TextButton color="primary" variant="outlined" type="submit">
            Invite
          </TextButton>
        </DialogActions>
      </form>
    </Modal>
  );
}
